import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

/* ─── Navigation config ───────────────────────────────────────── */
const NAV_LINKS = [
  { label: "HOME", to: "/" },
  { label: "ABOUT US", to: "/about-us" },
  { label: "MENU", to: "/menu" },
  { label: "FRANCHISE", to: "/franchise" },
  {
    label: "SERVICES",
    to: "/outdoor-catering",
    children: [
      { label: "Catering", to: "/outdoor-catering" },
      { label: "Barbequeen Express", to: "/barbequeen-express" },
      { label: "Events", to: "/events" },
    ],
  },
  { label: "OUTLETS", to: "/outlets" },
  {
    label: "GALLERY",
    to: "/outlet-gallery",
    children: [
      { label: "Outlet", to: "/outlet-gallery" },
      { label: "Fun Activities", to: "/fun-activities" },
      { label: "Awards & Achievements", to: "/awards-achievements" },
    ],
  },
  { label: "CONTACT US", to: "/contact-us" },
  { label: "BLOG", to: "/blog" },
];

/* ─── Dropdown component ──────────────────────────────────────── */
const Dropdown = ({ item, isActive, transparent }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const btnBase = "flex items-center gap-1 text-[13px] font-bold tracking-wide transition-all duration-200 whitespace-nowrap py-1 border-b-2";
  const btnColor = transparent
    ? isActive
      ? "text-[#ff8a96] border-[#ff8a96]"
      : "text-white border-transparent hover:text-[#ff8a96]"
    : isActive
      ? "text-[#9b1c2e] border-[#9b1c2e]"
      : "text-gray-800 border-transparent hover:text-[#9b1c2e]";

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={`${btnBase} ${btnColor}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {item.label}
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 pt-2 w-52 z-50">
          <div className="bg-white border border-gray-100 rounded-md shadow-xl overflow-hidden">
            {item.children.map((child) => (
              <Link
                key={child.to}
                to={child.to}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-[#9b1c2e] hover:text-white transition-colors duration-150"
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Mobile Accordion Item ───────────────────────────────────── */
const MobileNavItem = ({ item, isActive, onClose }) => {
  const [open, setOpen] = useState(false);

  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className={`flex items-center justify-between w-full px-4 py-3 text-[13px] font-bold tracking-wide border-b border-gray-100 ${isActive ? "text-[#9b1c2e]" : "text-gray-800"
            }`}
        >
          {item.label}
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && (
          <div className="bg-gray-50">
            {item.children.map((child) => (
              <Link
                key={child.to}
                to={child.to}
                onClick={onClose}
                className="block px-8 py-2.5 text-[12px] font-medium text-gray-600 hover:text-[#9b1c2e] border-b border-gray-100 last:border-0"
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={onClose}
      className={`block px-4 py-3 text-[13px] font-bold tracking-wide border-b border-gray-100 ${isActive ? "text-[#9b1c2e]" : "text-gray-800 hover:text-[#9b1c2e]"
        }`}
    >
      {item.label}
    </Link>
  );
};

/* ─── Main Header ─────────────────────────────────────────────── */
const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Become solid after scrolling down
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    handler(); // run once on mount
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  // Is the header currently transparent (over hero)?
  const transparent = !scrolled;

  return (
    <>
      {/* ── Fixed floating header ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${transparent
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-sm shadow-md"
          }`}
        style={{ fontFamily: "'Roboto', sans-serif" }}
      >
        {/* ── Top Info Bar (visible only when scrolled) ── */}
        <div
          className={`hidden sm:block border-b transition-all duration-300 ${transparent
            ? "border-white/10 bg-black/20"
            : "border-gray-100 bg-white"
            }`}
        >
          <div className="max-w-[1280px] mx-auto px-4 lg:px-8 h-9 flex items-center justify-between">
            {/* Left — Hours */}
            <div className={`flex items-center gap-1.5 text-[12px] ${transparent ? "text-white/70" : "text-gray-500"}`}>
              <svg className={`w-3.5 h-3.5 flex-shrink-0 ${transparent ? "text-white/50" : "text-gray-400"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Daily :{" "}
                <strong className={`font-semibold ${transparent ? "text-white/90" : "text-gray-700"}`}>11:00 am</strong>
                {" "}to{" "}
                <strong className={`font-semibold ${transparent ? "text-white/90" : "text-gray-700"}`}>11:00 pm</strong>
              </span>
            </div>

            {/* Right — Phone & Email */}
            <div className={`flex items-center gap-5 text-[12px] ${transparent ? "text-white/70" : "text-gray-500"}`}>
              <a
                href="tel:+919597228788"
                className={`flex items-center gap-1.5 transition-colors duration-200 ${transparent ? "hover:text-white" : "hover:text-[#9b1c2e]"}`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                +91-95972-28788
              </a>
              <span className={transparent ? "text-white/20" : "text-gray-300"}>◇</span>
              <a
                href="mailto:feedback@barbequeen.com"
                className={`flex items-center gap-1.5 transition-colors duration-200 ${transparent ? "hover:text-white" : "hover:text-[#9b1c2e]"}`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                feedback@barbequeen.com
              </a>
            </div>
          </div>
        </div>

        {/* ── Main Nav Bar ── */}
        <div className={`transition-colors duration-300 ${transparent ? "bg-transparent" : "bg-white"}`}>
          <div className="max-w-[1280px] mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between h-[70px] gap-4">

              {/* Logo */}
              <Link to="/" className="flex-shrink-0" aria-label="Barbequeen Home">
                <img
                  src={logo}
                  alt="Barbequeen — The Love of Delicacies"
                  className={`h-14 w-auto object-contain transition-all duration-300 ${transparent ? "brightness-0 invert" : ""}`}
                />
              </Link>

              {/* ── Desktop Navigation ── */}
              <nav
                className="hidden xl:flex items-center gap-6 flex-1 justify-center"
                aria-label="Main navigation"
              >
                {NAV_LINKS.map((item) => {
                  if (item.children) {
                    return (
                      <Dropdown
                        key={item.label}
                        item={item}
                        isActive={isActive(item.to)}
                        transparent={transparent}
                      />
                    );
                  }
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={`text-[13px] font-bold tracking-wide whitespace-nowrap transition-all duration-200 py-1 border-b-2 ${transparent
                        ? isActive(item.to)
                          ? "text-[#ff8a96] border-[#ff8a96]"
                          : "text-white border-transparent hover:text-[#ff8a96] hover:border-[#ff8a96]"
                        : isActive(item.to)
                          ? "text-[#9b1c2e] border-[#9b1c2e]"
                          : "text-gray-800 border-transparent hover:text-[#9b1c2e] hover:border-[#9b1c2e]"
                        }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              {/* ── Right side: Hamburger ── */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  id="header-mobile-menu-btn"
                  onClick={() => setMobileOpen((v) => !v)}
                  className={`xl:hidden flex flex-col justify-center items-center w-10 h-10 rounded-md border transition-colors duration-200 gap-[5px] flex-shrink-0 ${transparent
                    ? "border-white/30 hover:bg-white/10"
                    : "border-gray-200 hover:bg-gray-50"
                    }`}
                  aria-label="Toggle navigation menu"
                  aria-expanded={mobileOpen}
                >
                  <span className={`block h-0.5 rounded transition-all duration-300 ${transparent ? "bg-white" : "bg-[#9b1c2e]"} ${mobileOpen ? "w-5 rotate-45 translate-y-1.5" : "w-5"}`} />
                  <span className={`block h-0.5 rounded transition-all duration-300 ${transparent ? "bg-white" : "bg-[#9b1c2e]"} ${mobileOpen ? "opacity-0 w-0" : "w-4"}`} />
                  <span className={`block h-0.5 rounded transition-all duration-300 ${transparent ? "bg-white" : "bg-[#9b1c2e]"} ${mobileOpen ? "w-5 -rotate-45 -translate-y-1.5" : "w-5"}`} />
                </button>
              </div>

            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile Drawer Overlay ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 xl:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile Drawer Panel ── */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-80 max-w-[90vw] bg-white shadow-2xl xl:hidden transform transition-transform duration-300 ease-in-out flex flex-col ${mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <img src={logo} alt="Barbequeen" className="h-10 w-auto object-contain" />
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile Top Info */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex-shrink-0 space-y-1.5">
          <a href="tel:+919597228788" className="flex items-center gap-2 text-[12px] text-gray-600 hover:text-[#9b1c2e]">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            +91-95972-28788
          </a>
          <a href="mailto:feedback@barbequeen.com" className="flex items-center gap-2 text-[12px] text-gray-600 hover:text-[#9b1c2e]">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            feedback@barbequeen.com
          </a>
          <div className="flex items-center gap-2 text-[12px] text-gray-500">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Daily 11:00 am – 11:00 pm
          </div>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 overflow-y-auto" aria-label="Mobile navigation">
          {NAV_LINKS.map((item) => (
            <MobileNavItem
              key={item.label}
              item={item}
              isActive={isActive(item.to)}
              onClose={() => setMobileOpen(false)}
            />
          ))}
        </nav>
      </div>
    </>
  );
};

export default Header;

